import {
  AuthFlow,
  AuthProvider,
  ReauthError,
  ReauthErrorKind,
} from '@griply/types';
import {
  getAuthErrorCode,
  getAuthProvider,
  isRecentLogin,
  mapReauthError,
} from '@griply/utils';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';

import { getAuthentication, reportAuthError } from './helpers';

// Deliberate platform parallel: mobile reauths via native Google/Apple modules
// on a sync auth instance, web via browser redirects on the async JS SDK — only
// the error mapping (mapReauthError) is shareable.
export const getCurrentUserAuthProvider =
  async (): Promise<AuthProvider | null> => {
    const auth = await getAuthentication();
    return getAuthProvider(auth.currentUser?.providerData[0]?.providerId);
  };

// Lets the delete flow skip its reauth step when the user signed in seconds ago
// — notably right after returning from the Google/Apple redirect, which would
// otherwise bounce them straight back out again.
export const isCurrentLoginRecent = async (now: Date): Promise<boolean> => {
  const auth = await getAuthentication();
  return isRecentLogin(auth.currentUser?.metadata.lastSignInTime, now);
};

export const reauthenticateWithPassword = async (password: string) => {
  const auth = await getAuthentication();
  const email = auth.currentUser?.email;
  if (!auth.currentUser || !email) {
    throw new ReauthError(ReauthErrorKind.NoUser);
  }

  const credential = EmailAuthProvider.credential(email, password);
  try {
    await reauthenticateWithCredential(auth.currentUser, credential);
  } catch (error: unknown) {
    const kind = mapReauthError(getAuthErrorCode(error));
    if (kind === ReauthErrorKind.Unknown) {
      reportAuthError(error, AuthFlow.Reauthenticate);
    }
    throw new ReauthError(kind, { cause: error });
  }
};
